import React from 'react';
import styled from 'styled-components';
import { Building2, Users } from 'lucide-react';

const SelectionContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: ${({ theme }) => theme.spacing.lg};
`;

const SelectionCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  width: 100%;
  max-width: 600px;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: center;
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
`;

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const TypeButton = styled.button<{ color: string }>`
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.white};
  border: 3px solid ${({ color }) => color};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  min-height: 220px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.xl};
    background: ${({ color }) => color}10;
    border-color: ${({ color }) => color};
  }
  
  &:active {
    transform: translateY(-2px);
  }
`;

const IconWrapper = styled.div<{ color: string }>`
  width: 80px;
  height: 80px;
  background: ${({ color }) => color}20;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ButtonTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
`;

const ButtonDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  line-height: 1.5;
`;

interface UserTypeSelectionProps {
  onSelectType: (type: 'externo' | 'interno') => void;
}

const UserTypeSelection: React.FC<UserTypeSelectionProps> = ({ onSelectType }) => {
  return (
    <SelectionContainer>
      <SelectionCard>
        <Logo>
          <Title>Sistema de Cotizaciones</Title>
          <Subtitle>Selecciona tu tipo de usuario</Subtitle>
        </Logo>

        <ButtonGrid>
          <TypeButton 
            color="#3b82f6"
            onClick={() => onSelectType('externo')}
          >
            <IconWrapper color="#3b82f6">
              <Building2 size={40} color="#3b82f6" />
            </IconWrapper>
            <ButtonTitle>Proponente</ButtonTitle>
            <ButtonDescription>
              Soy un proveedor externo que cotiza servicios de transporte y logística
            </ButtonDescription>
          </TypeButton>

          <TypeButton 
            color="#10b981"
            onClick={() => onSelectType('interno')}
          >
            <IconWrapper color="#10b981">
              <Users size={40} color="#10b981" />
            </IconWrapper>
            <ButtonTitle>Administrador</ButtonTitle>
            <ButtonDescription>
              Soy parte del equipo interno de la empresa y gestiono cotizaciones
            </ButtonDescription>
          </TypeButton>
        </ButtonGrid>
      </SelectionCard>
    </SelectionContainer>
  );
};

export default UserTypeSelection;

